import axios, { AxiosError } from 'axios';
import type {
  AddressLookupResult,
  OwnerInfo,
  RegistrationBranch,
  TowerOption,
  ValidateUnitResult,
} from '../pages/public/registration/types';

/**
 * Cliente HTTP dedicado ao fluxo público de auto-cadastro (api-customer BFF).
 *
 * Diferente do `services/api.ts` (sessão Rails do smr-customer-ui), este cliente
 * fala com a api-customer nos endpoints PÚBLICOS `/api/v1/registration/*`. Não
 * usa o interceptor global que redireciona para /login em 401, pois aqui cada
 * passo trata o próprio erro. Base URL configurável por env; em dev o proxy do
 * Vite (`/api` → customer_api) resolve caminhos relativos.
 */
const BASE_URL = import.meta.env.VITE_API_CUSTOMER_URL || '';

const client = axios.create({
  baseURL: BASE_URL,
  headers: { Accept: 'application/json' },
  // Endpoints públicos: não enviam cookies de sessão.
  withCredentials: false,
  timeout: 30000,
});

const PREFIX = '/api/v1/registration';

/** Erro de negócio normalizado para a UI (mensagem amigável + status). */
export class RegistrationApiError extends Error {
  status?: number;
  /** Código de estado retornado pelo backend (ex.: 'doc_pending'). */
  code?: string;
  /** Titular ofuscado, presente quando `code === 'already_registered'`. */
  owner?: OwnerInfo;

  constructor(message: string, status?: number, code?: string, owner?: OwnerInfo) {
    super(message);
    this.name = 'RegistrationApiError';
    this.status = status;
    this.code = code;
    this.owner = owner;
  }
}

const DEFAULT_ERROR = 'Não foi possível concluir a operação. Tente novamente.';

/** Códigos de erro conhecidos do backend → mensagens amigáveis em pt-BR. */
const KNOWN_ERRORS: Record<string, string> = {
  captcha_failed: 'Falha na verificação anti-robô. Recarregue a página e tente novamente.',
  invalid_code: 'Código inválido. Confira os dígitos e tente novamente.',
  code_expired: 'O código expirou. Refaça o cadastro para receber um novo código.',
  too_many_attempts: 'Muitas tentativas. Aguarde alguns minutos e tente novamente.',
  document_pending: 'Seu documento ainda está em análise.',
  not_found: 'Não encontramos um cadastro com esses dados.',
  ownership_code_mismatch: 'RGI ou código de titularidade não conferem.',
  email_already_registered:
    'Este e-mail já está cadastrado. Clique em “Entrar” para acessar sua conta. Caso não se lembre da senha, clique em “Recuperar senha”.',
};

interface RawErrorBody {
  error?: string;
  message?: string;
  reason?: string;
  status?: string;
  owner?: { name?: string; email?: string };
}

function toApiError(err: unknown): RegistrationApiError {
  if (axios.isAxiosError(err)) {
    const axErr = err as AxiosError<RawErrorBody>;
    if (axErr.code === 'ECONNABORTED') {
      return new RegistrationApiError('A conexão expirou. Verifique sua internet e tente novamente.');
    }
    const data = axErr.response?.data;
    // O confirm sinaliza documento pendente via `message: "document_pending"`.
    const rawCode =
      data?.error ||
      (data?.message === 'document_pending' ? 'document_pending' : undefined) ||
      data?.reason;
    const friendly = rawCode ? KNOWN_ERRORS[rawCode] : undefined;
    const message = friendly || data?.error || data?.message || DEFAULT_ERROR;
    // `already_registered` traz o titular ofuscado; a mensagem final (com o
    // nome/e-mail interpolados e traduzidos) é montada na UI via i18n.
    const owner =
      data?.owner && (data.owner.name || data.owner.email)
        ? { name: data.owner.name ?? '', email: data.owner.email ?? '' }
        : undefined;
    return new RegistrationApiError(message, axErr.response?.status, rawCode || data?.status, owner);
  }
  return new RegistrationApiError(DEFAULT_ERROR);
}

// ---------------------------------------------------------------------------
// Mapeamento snake_case (backend) → camelCase (frontend)
// ---------------------------------------------------------------------------

interface RawUnit {
  id: string | number;
  label?: string;
  unit?: string;
}

interface RawTower {
  id: string | number;
  name?: string;
  code?: string;
  units?: RawUnit[];
}

interface RawAddressLookup {
  registration_id: string;
  branch?: RegistrationBranch;
  type?: RegistrationBranch;
  address?: {
    street?: string;
    neighborhood?: string;
    city?: string;
    state?: string;
    zip_code?: string;
    number?: string;
  };
  towers?: RawTower[];
  commercial_services?: RawTower[];
}

function mapTowers(raw?: RawTower[]): TowerOption[] {
  return (raw || []).map((t) => ({
    id: String(t.id),
    name: t.name || t.code || `Torre ${t.id}`,
    units: (t.units || []).map((u) => ({
      id: String(u.id),
      label: u.label || u.unit || String(u.id)
    })),
  }));
}

function mapAddressLookup(raw: RawAddressLookup): AddressLookupResult {
  const branch: RegistrationBranch = raw.branch || raw.type || 'utility';
  const addr = raw.address || {};
  return {
    registrationId: raw.registration_id,
    branch,
    address: {
      street: addr.street || '',
      neighborhood: addr.neighborhood,
      city: addr.city,
      state: addr.state,
      zipCode: addr.zip_code || '',
      number: addr.number || '',
    },
    towers: branch === 'condominium' ? mapTowers(raw.towers || raw.commercial_services) : undefined,
  };
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

export interface AddressLookupParams {
  zipCode: string;
  number: string;
  captchaToken: string;
}

/** Passo 1: consulta endereço por CEP + número (protegido por captcha). */
export async function addressLookup(params: AddressLookupParams): Promise<AddressLookupResult> {
  try {
    const { data } = await client.post<RawAddressLookup>(`${PREFIX}/address_lookup`, {
      zip_code: params.zipCode,
      number: params.number,
      captcha_token: params.captchaToken,
    });
    return mapAddressLookup(data);
  } catch (err) {
    throw toApiError(err);
  }
}

export interface ValidateCondoUnitParams {
  registrationId: string;
  commercialServiceId: string;
  unitId: string;
}

export interface ValidateUtilityParams {
  registrationId: string;
  rgi: string;
  ownershipCode: string;
}

interface RawValidate {
  ok?: boolean;
  valid?: boolean;
  document_required?: boolean;
  requires_document?: boolean;
  message?: string;
}

function mapValidate(raw: RawValidate): ValidateUnitResult {
  return {
    ok: raw.ok ?? raw.valid ?? false,
    documentRequired: raw.document_required ?? raw.requires_document ?? false,
    message: raw.message,
  };
}

/** Passo 2 (condomínio): valida a unidade selecionada. */
export async function validateCondoUnit(params: ValidateCondoUnitParams): Promise<ValidateUnitResult> {
  try {
    const { data } = await client.post<RawValidate>(`${PREFIX}/validate_unit`, {
      registration_id: params.registrationId,
      commercial_service_id: params.commercialServiceId,
      unit: params.unitId,
    });
    return mapValidate(data);
  } catch (err) {
    throw toApiError(err);
  }
}

/**
 * Passo 2 (concessionária): valida RGI + código de titularidade.
 * O código nunca retorna ao frontend; validação é server-side.
 */
export async function validateUtility(params: ValidateUtilityParams): Promise<ValidateUnitResult> {
  try {
    const { data } = await client.post<RawValidate>(`${PREFIX}/validate_unit`, {
      registration_id: params.registrationId,
      rgi: params.rgi,
      ownership_code: params.ownershipCode,
    });
    return mapValidate(data);
  } catch (err) {
    throw toApiError(err);
  }
}

export interface CreateRegistrationParams {
  registrationId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  password: string;
}

/** Passo 3: cria o usuário não confirmado e dispara o código por e-mail. */
export async function createRegistration(params: CreateRegistrationParams): Promise<void> {
  try {
    await client.post(`${PREFIX}`, {
      registration_id: params.registrationId,
      first_name: params.firstName,
      last_name: params.lastName,
      email: params.email,
      phone_number: params.phone,
      password: params.password,
      password_confirmation: params.password,
    });
  } catch (err) {
    throw toApiError(err);
  }
}

export interface UploadDocumentParams {
  registrationId: string;
  file: File;
}

/** Passo 4 (condicional): envia o documento comprobatório (multipart → S3). */
export async function uploadDocument(params: UploadDocumentParams): Promise<void> {
  const form = new FormData();
  form.append('registration_id', params.registrationId);
  form.append('file', params.file);
  try {
    await client.post(`${PREFIX}/document`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  } catch (err) {
    throw toApiError(err);
  }
}

export interface ConfirmParams {
  email: string;
  code: string;
}

/** Passo 5: confirma o cadastro (código válido + documento aprovado/dispensado). */
export async function confirmRegistration(params: ConfirmParams): Promise<void> {
  try {
    await client.post(`${PREFIX}/confirm`, {
      email: params.email,
      code: params.code,
    });
  } catch (err) {
    throw toApiError(err);
  }
}
